import React, { useState, useEffect } from 'react';
import axios from 'axios';
import UserLayout from '../../components/user/UserLayout';
import toast from 'react-hot-toast';
import { Wallet, ArrowUpRight, ArrowDownLeft, ArrowLeftRight, Filter, Calendar } from 'lucide-react';

export default function WalletPage() {
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  // Change 2: date filter
  const [filters, setFilters] = useState({ walletType: '', startDate: '', endDate: '' });
  // Change 3: transfer form
  const [transferAmount, setTransferAmount] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);

  const fetchBalance = () => axios.get('/api/wallet/balance').then(r => setBalance(r.data));

  const fetchTransactions = () => {
    const params = new URLSearchParams({ page, limit: 20 });
    if (filters.walletType) params.append('walletType', filters.walletType);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    axios.get(`/api/wallet/transactions?${params}`)
      .then(r => { setTransactions(r.data.transactions); setTotal(r.data.total); });
  };

  useEffect(() => { fetchBalance(); }, []);
  useEffect(() => { fetchTransactions(); }, [page, filters]);

  const handleTransfer = async () => {
    const amt = parseFloat(transferAmount);
    if (!amt || amt <= 0) return toast.error('Enter valid amount');
    setTransferLoading(true);
    try {
      const { data } = await axios.post('/api/wallet/transfer-to-main', { amount: amt });
      toast.success(data.message);
      setTransferAmount('');
      setShowTransfer(false);
      fetchBalance();
      fetchTransactions();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Transfer failed');
    } finally {
      setTransferLoading(false);
    }
  };

  const typeIcon = (type) => {
    const credit = ['wallet_topup','referral_commission','profit_commission','achievement_bonus','admin_credit'];
    if (type === 'wallet_transfer') return <ArrowLeftRight className="w-4 h-4 text-blue-400" />;
    if (credit.includes(type)) return <ArrowDownLeft className="w-4 h-4 text-primary-400" />;
    return <ArrowUpRight className="w-4 h-4 text-red-400" />;
  };

  const isCredit = (type) => ['wallet_topup','referral_commission','profit_commission','achievement_bonus','admin_credit'].includes(type);

  return (
    <UserLayout>
      <div className="animate-fade-in">
        <h1 className="text-2xl font-bold text-white mb-6">Wallet</h1>

        {/* Wallet Cards */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div className="bg-dark-700 border border-white/5 rounded-2xl p-6">
            <Wallet className="w-8 h-8 text-primary-400 mb-3" />
            <p className="text-gray-400 text-sm">Main Wallet</p>
            <p className="text-3xl font-bold text-white mt-1">${(balance?.mainWallet || 0).toFixed(2)}</p>
            <p className="text-xs text-gray-500 mt-1">Service charges & profit deductions</p>
            <div className={`mt-2 flex items-center gap-1.5 text-xs ${balance?.isActive ? 'text-primary-400' : 'text-red-400'}`}>
              <span className={`w-2 h-2 rounded-full ${balance?.isActive ? 'bg-primary-500' : 'bg-red-500'}`}></span>
              {balance?.isActive ? 'Account Active' : 'Account Inactive – Recharge Needed'}
            </div>
          </div>

          <div className="bg-dark-700 border border-white/5 rounded-2xl p-6">
            <Wallet className="w-8 h-8 text-green-400 mb-3" />
            <p className="text-gray-400 text-sm">Commission Wallet</p>
            <p className="text-3xl font-bold text-white mt-1">${(balance?.commissionWallet || 0).toFixed(2)}</p>
            <p className="text-xs text-gray-500 mt-1">Referral & profit commissions</p>
            {/* Change 3: Transfer to Main button */}
            <button onClick={() => setShowTransfer(!showTransfer)}
              className="mt-3 flex items-center gap-2 bg-blue-600/20 border border-blue-500/30 text-blue-300 hover:bg-blue-600/30 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors">
              <ArrowLeftRight className="w-3.5 h-3.5" />
              Transfer to Main Wallet
            </button>
          </div>
        </div>

        {/* Transfer Panel */}
        {showTransfer && (
          <div className="bg-dark-700 border border-blue-500/30 rounded-2xl p-5 mb-6 animate-slide-up">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <ArrowLeftRight className="w-4 h-4 text-blue-400" />
              Transfer Commission → Main Wallet
            </h3>
            <p className="text-gray-500 text-xs mb-4">Available: ${(balance?.commissionWallet || 0).toFixed(2)}</p>
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                <input
                  type="number"
                  value={transferAmount}
                  onChange={e => setTransferAmount(e.target.value)}
                  className="w-full bg-dark-600 border border-white/10 text-white rounded-xl pl-8 pr-4 py-3 focus:outline-none focus:border-blue-500"
                  placeholder="Enter amount"
                  max={balance?.commissionWallet}
                />
              </div>
              <button onClick={handleTransfer} disabled={transferLoading || !transferAmount}
                className="bg-blue-600 hover:bg-blue-500 disabled:bg-blue-900 text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2 transition-colors">
                {transferLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : 'Transfer'}
              </button>
            </div>
            <div className="flex gap-2 mt-3">
              {[100, 500, 1000].filter(a => a <= (balance?.commissionWallet || 0)).map(a => (
                <button key={a} onClick={() => setTransferAmount(String(a))}
                  className="px-3 py-1 bg-dark-600 border border-white/10 text-gray-300 hover:text-white rounded-lg text-xs transition-colors">
                  ${a}
                </button>
              ))}
              {(balance?.commissionWallet || 0) > 0 && (
                <button onClick={() => setTransferAmount(String(Math.floor(balance.commissionWallet)))}
                  className="px-3 py-1 bg-dark-600 border border-white/10 text-gray-300 hover:text-white rounded-lg text-xs transition-colors">
                  Max
                </button>
              )}
            </div>
          </div>
        )}

        {/* Change 2: Filters with date */}
        <div className="bg-dark-700 border border-white/5 rounded-2xl p-4 mb-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Wallet</label>
              <select value={filters.walletType} onChange={e => setFilters(f => ({ ...f, walletType: e.target.value }))}
                className="bg-dark-600 border border-white/10 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-primary-500">
                <option value="">All Wallets</option>
                <option value="main">Main Wallet</option>
                <option value="commission">Commission Wallet</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">From Date</label>
              <input type="date" value={filters.startDate} onChange={e => setFilters(f => ({ ...f, startDate: e.target.value }))}
                className="bg-dark-600 border border-white/10 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-primary-500" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">To Date</label>
              <input type="date" value={filters.endDate} onChange={e => setFilters(f => ({ ...f, endDate: e.target.value }))}
                className="bg-dark-600 border border-white/10 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-primary-500" />
            </div>
            <button onClick={() => setFilters({ walletType: '', startDate: '', endDate: '' })}
              className="px-3 py-2 bg-dark-600 border border-white/10 text-gray-400 hover:text-white rounded-xl text-sm transition-colors">
              Clear
            </button>
          </div>
        </div>

        {/* Transactions */}
        <div className="bg-dark-700 border border-white/5 rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-white/5">
            <h3 className="text-white font-semibold">Transaction History ({total})</h3>
          </div>
          <div className="divide-y divide-white/5">
            {transactions.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No transaction history. found</p>
            ) : transactions.map(t => (
              <div key={t._id} className="flex items-center gap-3 px-4 py-3">
                <div className="w-8 h-8 rounded-lg bg-dark-600 flex items-center justify-center flex-shrink-0">
                  {typeIcon(t.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate capitalize">{(t.description || t.type).replace(/_/g, ' ')}</p>
                  <p className="text-gray-500 text-xs">{new Date(t.createdAt).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className={`font-semibold text-sm ${isCredit(t.type) ? 'text-primary-400' : 'text-red-400'}`}>
                    {isCredit(t.type) ? '+' : '-'}${t.amount.toFixed(2)}
                  </p>
                  <p className="text-gray-500 text-xs capitalize">{t.walletType}</p>
                </div>
              </div>
            ))}
          </div>
          {total > 20 && (
            <div className="p-4 border-t border-white/5 flex justify-between items-center">
              <p className="text-gray-400 text-sm">Page {page}</p>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1} className="px-3 py-1.5 bg-dark-600 text-white rounded-lg text-sm disabled:opacity-50">Prev</button>
                <button onClick={() => setPage(p => p+1)} disabled={page*20>=total} className="px-3 py-1.5 bg-dark-600 text-white rounded-lg text-sm disabled:opacity-50">Next</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </UserLayout>
  );
}
